import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { useSupabaseData } from '../contexts/SupabaseDataContext';
import { checkR2Status } from '../services/storageService';
import { saveAdminCategory } from '../services/stripeTaxMappingService';
import { fetchAdminOrdersWithRefunds as adminFetchAllOrders } from '../services/adminOrderService';
import { fetchAdminOverviewMetrics } from '../services/adminOverviewService';
import {
  fetchAdminUserStatuses,
  sendAdminPasswordResetEmail,
  setAdminUserDisabled
} from '../services/adminUserManagementService';
import AdminOrderDetailsModal from '../components/admin/AdminOrderDetailsModal';
import AdminTablePagination from '../components/admin/AdminTablePagination';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiShoppingBag, FiTrendingUp, FiTrash2, FiBox, FiDatabase, FiCloud, FiCheckCircle, FiAlertCircle, FiLoader, FiShield, FiSearch, FiStar, FiEye, FiEyeOff, FiFilter, FiCreditCard, FiDollarSign, FiPieChart, FiActivity, FiBarChart2, FiLayers, FiPlus, FiTag, FiX, FiEdit2, FiSave, FiLock, FiUnlock, FiMail } = FiIcons;

const formatPrice = (price) => {
  if (price === undefined || price === null) return '$0.00';
  const val = typeof price === 'string' ? price.replace('$', '') : price;
  return `$${parseFloat(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const emptyCategoryEditor = {
  id: '',
  name: '',
  slug: '',
  icon_name: 'Box',
  stripe_product_tax_code: ''
};

const AdminDashboardPage = () => {
  const { user, profile, loading: authLoading, isInitialized } = useSupabaseAuth();
  const {
    sellers, offerings: publicOfferings, categories,
    adminFetchUsers, adminUpdateUserRole, adminDeleteUser,
    adminFetchOfferings, adminToggleOffering, adminDeleteOffering,
    adminFetchReviews, adminDeleteReview,
    adminFetchFinanceSummary, adminFetchTransactions,
    adminFetchSellerAnalytics,
    adminAddCategory, adminDeleteCategory
  } = useSupabaseData();

  const [activeTab, setActiveTab] = useState('overview');
  const [r2Status, setR2Status] = useState({ loading: true, connected: false, message: '' });

  // Data States
  const [allUsers, setAllUsers] = useState([]);
  const [userStatuses, setUserStatuses] = useState({});
  const [allOfferings, setAllOfferings] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [overviewMetrics, setOverviewMetrics] = useState(null);
  const [financeSummary, setFinanceSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [sellerAnalytics, setSellerAnalytics] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [userDeletionTarget, setUserDeletionTarget] = useState(null);
  const [userDeletionPin, setUserDeletionPin] = useState('');
  const [userDeletionEmail, setUserDeletionEmail] = useState('');
  const [userDeletionError, setUserDeletionError] = useState('');
  const [userDeletionLoading, setUserDeletionLoading] = useState(false);

  // Category Form
  const [newCat, setNewCat] = useState({ name: '', slug: '', icon: 'Box' });
  const [isCatModalOpen, setIsCatModalOpen] = useState(false);
  const [categoryEditor, setCategoryEditor] = useState(emptyCategoryEditor);
  const [isCategoryEditorOpen, setIsCategoryEditorOpen] = useState(false);
  const [categorySaving, setCategorySaving] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [orderFilters, setOrderFilters] = useState({
    startDate: '', endDate: '', sellerName: '', buyerName: '', status: 'all'
  });

  const loadTabData = useCallback(async () => {
    if (!isInitialized || !profile || profile.role !== 'admin') return;
    setTabLoading(true);
    try {
      if (activeTab === 'users') {
        const [data, statusRows] = await Promise.all([
          adminFetchUsers(),
          fetchAdminUserStatuses()
        ]);
        setAllUsers(data || []);
        setUserStatuses((statusRows || []).reduce((statuses, row) => {
          statuses[row.id] = row.disabled;
          return statuses;
        }, {}));
      } else if (activeTab === 'listings') {
        const data = await adminFetchOfferings();
        setAllOfferings(data || []);
      } else if (activeTab === 'reviews') {
        const data = await adminFetchReviews();
        setAllReviews(data || []);
      } else if (activeTab === 'finance') {
        const [summary, txns] = await Promise.all([
          adminFetchFinanceSummary(),
          adminFetchTransactions()
        ]);
        setFinanceSummary(summary);
        setTransactions(txns || []);
      } else if (activeTab === 'analytics') {
        const data = await adminFetchSellerAnalytics();
        setSellerAnalytics(data || []);
      } else if (activeTab === 'orders') {
        const data = await adminFetchAllOrders();
        setAllOrders(data || []);
      }
    } catch (err) {
      console.error("Admin tab load error:", err);
    } finally {
      setTabLoading(false);
    }
  }, [activeTab, isInitialized, profile, adminFetchUsers, adminFetchOfferings, adminFetchReviews, adminFetchFinanceSummary, adminFetchTransactions, adminFetchSellerAnalytics, adminFetchAllOrders]);

  useEffect(() => {
    loadTabData();
  }, [activeTab, isInitialized, profile, loadTabData]);

  useEffect(() => {
    if (!isInitialized || profile?.role !== 'admin') return undefined;

    let active = true;

    fetchAdminOverviewMetrics()
      .then((metrics) => {
        if (active) setOverviewMetrics(metrics);
      })
      .catch((error) => {
        console.error('Admin overview metrics load error:', error);
      });

    return () => {
      active = false;
    };
  }, [isInitialized, profile]);

  useEffect(() => {
    const verifyR2 = async () => {
      const status = await checkR2Status();
      setR2Status({ loading: false, ...status });
    };
    verifyR2();
  }, []);

  const filteredOrders = useMemo(() => {
    return allOrders.filter(o => {
      const matchesSeller = o.seller_business_name.toLowerCase().includes(orderFilters.sellerName.toLowerCase());
      const matchesBuyer = o.buyer_name.toLowerCase().includes(orderFilters.buyerName.toLowerCase()) || o.buyer_email.toLowerCase().includes(orderFilters.buyerName.toLowerCase());
      const matchesStatus = orderFilters.status === 'all' || o.status === orderFilters.status;
      const orderDate = new Date(o.created_at).getTime();
      const matchesStart = !orderFilters.startDate || orderDate >= new Date(orderFilters.startDate).getTime();
      const matchesEnd = !orderFilters.endDate || orderDate <= (new Date(orderFilters.endDate).getTime() + 86400000);
      return matchesSeller && matchesBuyer && matchesStatus && matchesStart && matchesEnd;
    });
  }, [allOrders, orderFilters]);

  const openOrderDetails = useCallback(async (orderId) => {
    const existingOrder = allOrders.find(order => order.id === orderId);

    if (existingOrder) {
      setSelectedOrder(existingOrder);
      return;
    }

    try {
      const data = await adminFetchAllOrders();
      setAllOrders(data || []);
      setSelectedOrder((data || []).find(order => order.id === orderId) || null);
    } catch (error) {
      alert(error.message);
    }
  }, [adminFetchAllOrders, allOrders]);

  // Category Actions
  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      await adminAddCategory(newCat.name, newCat.slug, newCat.icon);
      setNewCat({ name: '', slug: '', icon: 'Box' });
      setIsCatModalOpen(false);
    } catch (err) { alert(err.message); }
  };

  const handleEditCategory = (category) => {
    setCategoryEditor({
      id: category.id,
      name: category.name || '',
      slug: category.slug || '',
      icon_name: category.icon_name || 'Box',
      stripe_product_tax_code: category.stripe_product_tax_code || ''
    });
    setIsCategoryEditorOpen(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    setCategorySaving(true);
    try {
      await saveAdminCategory(categoryEditor);
      setIsCategoryEditorOpen(false);
      window.location.reload();
    } catch (err) {
      alert(err.message);
    } finally {
      setCategorySaving(false);
    }
  };

  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"? This will not delete listings but they will lose their category label.`)) return;
    try {
      await adminDeleteCategory(id);
    } catch (err) { alert(err.message); }
  };

  // Action Handlers
  const handleToggleListing = async (offering) => {
    const action = offering.is_active ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} "${offering.title}"?`)) return;
    try {
      await adminToggleOffering(offering.id, !offering.is_active);
      await loadTabData();
    } catch (err) { alert(err.message); }
  };

  const handleDeleteListing = async (offeringId, title) => {
    if (!window.confirm(`⚠️ PERMANENT DELETE: Are you sure you want to delete the listing "${title}"? This cannot be undone.`)) return;
    try {
      await adminDeleteOffering(offeringId);
      await loadTabData();
    } catch (err) { alert(err.message); }
  };

  const handleUpdateRole = async (userId, role, isSeller) => {
    if (!window.confirm(`Change user role to ${role}?`)) return;
    try {
      await adminUpdateUserRole(userId, role, isSeller);
      await loadTabData();
    } catch (err) { alert(err.message); }
  };

  const handleToggleUserDisabled = async (target) => {
    const currentlyDisabled = Boolean(userStatuses[target.id]);
    const nextDisabled = !currentlyDisabled;
    const action = nextDisabled ? 'disable sign-in for' : 'restore sign-in for';

    if (!window.confirm(`Are you sure you want to ${action} ${target.name} (${target.email})?`)) return;

    try {
      const result = await setAdminUserDisabled(target.id, nextDisabled);
      setUserStatuses((current) => ({
        ...current,
        [target.id]: Boolean(result.disabled)
      }));
    } catch (err) {
      alert(err.message || 'Unable to update user access.');
    }
  };

  const handleSendPasswordReset = async (target) => {
    if (!window.confirm(`Send a password reset email to ${target.email}?`)) return;

    try {
      await sendAdminPasswordResetEmail(target.email);
      alert(`Password reset email sent to ${target.email}.`);
    } catch (err) {
      alert(err.message || 'Unable to send the password reset email.');
    }
  };

  const handleDeleteUser = (target) => {
    setUserDeletionTarget(target);
    setUserDeletionPin('');
    setUserDeletionEmail('');
    setUserDeletionError('');
  };

  const handleConfirmDeleteUser = async (event) => {
    event.preventDefault();
    if (!userDeletionTarget) return;

    setUserDeletionLoading(true);
    setUserDeletionError('');

    try {
      await adminDeleteUser(
        userDeletionTarget.id,
        userDeletionPin,
        userDeletionEmail
      );
      setUserDeletionTarget(null);
      setUserDeletionPin('');
      setUserDeletionEmail('');
      await loadTabData();
    } catch (err) {
      setUserDeletionError(err.message || 'Unable to delete this account.');
    } finally {
      setUserDeletionLoading(false);
    }
  };

  const closeUserDeletionModal = () => {
    if (userDeletionLoading) return;
    setUserDeletionTarget(null);
    setUserDeletionPin('');
    setUserDeletionEmail('');
    setUserDeletionError('');
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Delete this customer review permanently?')) return;
    try {
      await adminDeleteReview(reviewId);
      await loadTabData();
    } catch (err) { alert(err.message); }
  };

  if (!isInitialized || authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center bg-white space-y-4">
        <FiLoader className="w-12 h-12 text-indigo-600 animate-spin" />
        <p className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.3em]">Authenticating Admin...</p>
      </div>
    );
  }

  if (!user || profile?.role !== 'admin') {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white p-10 rounded-[2.5rem] shadow-xl text-center border border-gray-100">
          <SafeIcon icon={FiShield} className="text-4xl text-red-600 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-500 font-medium mb-8">Administrator authorization required.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', icon: FiActivity, label: 'Stats' },
    { id: 'finance', icon: FiPieChart, label: 'Finance' },
    { id: 'orders', icon: FiShoppingBag, label: 'Orders' },
    { id: 'analytics', icon: FiBarChart2, label: 'Sellers' },
    { id: 'categories', icon: FiLayers, label: 'Categories' },
    { id: 'users', icon: FiUsers, label: 'Users' },
    { id: 'listings', icon: FiBox, label: 'Listings' },
    { id: 'reviews', icon: FiStar, label: 'Reviews' },
    { id: 'infrastructure', icon: FiDatabase, label: 'System' }
  ];

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-black text-gray-900 mb-1 tracking-tight">Admin Portal</h1>
            <p className="text-slate-500 font-bold uppercase text-[10px] tracking-widest">Platform Command Center</p>
          </div>
          <div className="flex bg-white p-1.5 rounded-2xl shadow-sm border border-gray-100 overflow-x-auto no-scrollbar">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-lg' : 'text-gray-400 hover:bg-gray-50'}`}
              >
                <SafeIcon icon={tab.icon} className="mr-2 text-sm" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                {[
                  { name: 'Total Sellers', val: sellers.length, icon: FiUsers, color: 'text-blue-600 bg-blue-100' },
                  { name: 'Global Orders', val: overviewMetrics?.total_orders ?? '—', icon: FiShoppingBag, color: 'text-emerald-600 bg-emerald-100' },
                  { name: 'Market Items', val: publicOfferings.length, icon: FiBox, color: 'text-purple-600 bg-purple-100' },
                  { name: 'Categories', val: categories.length, icon: FiLayers, color: 'text-indigo-600 bg-indigo-100' },
                  { name: 'Open Disputes', val: overviewMetrics?.open_disputes ?? '—', icon: FiAlertCircle, color: 'text-rose-600 bg-rose-100' },
                  { name: 'New Disputes (7 Days)', val: overviewMetrics?.new_disputes_7d ?? '—', icon: FiTrendingUp, color: 'text-amber-600 bg-amber-100' },
                  { name: 'New Clients (7 Days)', val: overviewMetrics?.new_clients_7d ?? '—', icon: FiUsers, color: 'text-cyan-600 bg-cyan-100' },
                  { name: 'Stripe Payment Disputes', val: overviewMetrics?.stripe_payment_disputes ?? '—', icon: FiCreditCard, color: 'text-violet-600 bg-violet-100' }
                ].map(stat => (
                  <div key={stat.name} className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${stat.color}`}>
                      <SafeIcon icon={stat.icon} />
                    </div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{stat.name}</p>
                    <p className="text-3xl font-black text-gray-900">{stat.val}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'categories' && (
            <motion.div key="categories" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-100 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-black text-gray-900">Marketplace Categories</h3>
                  <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Define product taxonomy</p>
                </div>
                <button onClick={() => setIsCatModalOpen(true)} className="flex items-center px-6 py-3 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-100">
                  <FiPlus className="mr-2" /> Add Category
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {categories.map(cat => (
                  <div key={cat.id} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm group hover:border-indigo-100 transition-all">
                    <div className="flex justify-between items-start mb-4">
                      <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600">
                        <SafeIcon name={cat.icon_name || 'Box'} />
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleEditCategory(cat)}
                          className="p-2 text-indigo-500 hover:text-indigo-700 transition-all"
                          aria-label={`Edit ${cat.name}`}
                          title={`Edit ${cat.name}`}
                        >
                          <SafeIcon icon={FiEdit2} />
                        </button>
                        <button onClick={() => handleDeleteCategory(cat.id, cat.name)} className="p-2 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                    <h4 className="text-lg font-black text-gray-900">{cat.name}</h4>
                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1">Slug: {cat.slug}</p>
                    <div className="mt-4 rounded-2xl bg-indigo-50 p-4">
                      <p className="text-[9px] font-black uppercase tracking-widest text-indigo-500">Stripe Tax code</p>
                      <p className="mt-1 break-all font-mono text-sm font-black text-indigo-700">
                        {cat.stripe_product_tax_code || 'Not configured'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {isCategoryEditorOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-[100] p-4">
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] max-w-lg w-full p-10 shadow-2xl">
                    <div className="flex justify-between items-center mb-8">
                      <div>
                        <h3 className="text-2xl font-black text-gray-900">Edit Category</h3>
                        <p className="text-xs text-slate-400 font-bold mt-1">Assign the Stripe Tax product code used by automatic tax.</p>
                      </div>
                      <button onClick={() => setIsCategoryEditorOpen(false)} className="text-slate-400 hover:text-gray-900">
                        <SafeIcon icon={FiX} className="text-2xl" />
                      </button>
                    </div>
                    <form onSubmit={handleSaveCategory} className="space-y-6">
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Display Name</label>
                        <input type="text" required value={categoryEditor.name} onChange={e => setCategoryEditor({...categoryEditor, name: e.target.value})} className="w-full px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold border-none outline-none focus:ring-2 focus:ring-indigo-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">URL Slug</label>
                        <input type="text" required pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$" value={categoryEditor.slug} onChange={e => setCategoryEditor({...categoryEditor, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})} className="w-full px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold border-none outline-none focus:ring-2 focus:ring-indigo-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Icon Identifier (Feather)</label>
                        <input type="text" required value={categoryEditor.icon_name} onChange={e => setCategoryEditor({...categoryEditor, icon_name: e.target.value})} className="w-full px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold border-none outline-none focus:ring-2 focus:ring-indigo-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-2">Stripe Tax Product Code</label>
                        <input type="text" required pattern="txcd_[0-9]+" title="Enter a Stripe Tax product code such as txcd_99999999." placeholder="e.g., txcd_99999999" value={categoryEditor.stripe_product_tax_code} onChange={e => setCategoryEditor({...categoryEditor, stripe_product_tax_code: e.target.value.toLowerCase()})} className="w-full px-5 py-4 bg-indigo-50 rounded-2xl text-sm font-mono font-black text-indigo-700 border-2 border-indigo-100 outline-none focus:ring-2 focus:ring-indigo-500" />
                      </div>
                      <button disabled={categorySaving} type="submit" className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-100 disabled:opacity-50">
                        <SafeIcon icon={categorySaving ? FiLoader : FiSave} className={categorySaving ? 'mr-2 inline animate-spin' : 'mr-2 inline'} />
                        {categorySaving ? 'Saving...' : 'Save Category and Tax Code'}
                      </button>
                    </form>
                  </motion.div>
                </div>
              )}

              {isCatModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-[100] p-4">
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] max-w-md w-full p-10 shadow-2xl">
                    <div className="flex justify-between items-center mb-8">
                      <h3 className="text-2xl font-black text-gray-900">New Category</h3>
                      <button onClick={() => setIsCatModalOpen(false)} className="text-slate-400 hover:text-gray-900"><FiX size={24} /></button>
                    </div>
                    <form onSubmit={handleAddCategory} className="space-y-6">
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Display Name</label>
                        <input type="text" required value={newCat.name} onChange={e => setNewCat({...newCat, name: e.target.value})} className="w-full px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold border-none outline-none focus:ring-2 focus:ring-indigo-500" placeholder="e.g., Pies & Tarts" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">URL Slug</label>
                        <input type="text" required value={newCat.slug} onChange={e => setNewCat({...newCat, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})} className="w-full px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold border-none outline-none focus:ring-2 focus:ring-indigo-500" placeholder="e.g., pies-tarts" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Icon Identifier (Feather)</label>
                        <input type="text" required value={newCat.icon} onChange={e => setNewCat({...newCat, icon: e.target.value})} className="w-full px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold border-none outline-none focus:ring-2 focus:ring-indigo-500" placeholder="e.g., ShoppingBag" />
                      </div>
                      <button type="submit" className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-100">
                        Create Category
                      </button>
                    </form>
                  </motion.div>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'orders' && (
            <motion.div key="orders" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-100">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600">
                    <SafeIcon icon={FiFilter} />
                  </div>
                  <h3 className="text-lg font-black text-gray-900">Advanced Audit Filters</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Merchant</label>
                    <input type="text" placeholder="Bakery name..." value={orderFilters.sellerName} onChange={e => setOrderFilters({...orderFilters, sellerName: e.target.value})} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm font-bold border-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Customer</label>
                    <input type="text" placeholder="Name or email..." value={orderFilters.buyerName} onChange={e => setOrderFilters({...orderFilters, buyerName: e.target.value})} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm font-bold border-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">From Date</label>
                    <input type="date" value={orderFilters.startDate} onChange={e => setOrderFilters({...orderFilters, startDate: e.target.value})} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm font-bold border-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">To Date</label>
                    <input type="date" value={orderFilters.endDate} onChange={e => setOrderFilters({...orderFilters, endDate: e.target.value})} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm font-bold border-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Fulfillment Status</label>
                    <select value={orderFilters.status} onChange={e => setOrderFilters({...orderFilters, status: e.target.value})} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-[10px] font-black uppercase tracking-widest border-none focus:ring-2 focus:ring-indigo-500 appearance-none">
                      <option value="all">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="accepted">Accepted</option>
                      <option value="preparing">In Kitchen</option>
                      <option value="ready">Ready</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                <AdminTablePagination>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50/50 text-[10px] text-gray-400 uppercase tracking-widest">
                          <th className="px-8 py-4 text-left">Order Details</th>
                          <th className="px-8 py-4 text-left">Parties</th>
                          <th className="px-8 py-4 text-left">Mode / Method</th>
                          <th className="px-8 py-4 text-left">Total</th>
                          <th className="px-8 py-4 text-left">Refund</th>
                          <th className="px-8 py-4 text-right">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {tabLoading ? (
                          <tr><td colSpan="6" className="py-20 text-center"><FiLoader className="animate-spin inline text-indigo-300 text-3xl" /></td></tr>
                        ) : filteredOrders.length === 0 ? (
                          <tr><td colSpan="6" className="py-20 text-center text-gray-400 font-bold uppercase text-[10px] tracking-widest">No matching orders found</td></tr>
                        ) : filteredOrders.map(o => (
                          <tr
                            key={o.id}
                            onClick={() => openOrderDetails(o.id)}
                            className="cursor-pointer hover:bg-indigo-50/40"
                          >
                            <td className="px-8 py-5">
                              <p className="font-bold text-gray-900 text-sm">#{o.id.slice(0,8).toUpperCase()}</p>
                              <p className="text-[9px] text-indigo-400 font-black uppercase mt-1">{new Date(o.created_at).toLocaleDateString()} {new Date(o.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                              <p className="text-[10px] text-gray-400 font-medium mt-1 truncate max-w-[150px]">
                                {o.items?.map(i => `${i.quantity}x ${i.title}`).join(', ')}
                              </p>
                            </td>
                            <td className="px-8 py-5">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-[9px] font-black text-slate-400 uppercase">Seller:</span>
                                  <span className="text-sm font-bold text-gray-900">{o.seller_business_name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-[9px] font-black text-slate-400 uppercase">Buyer:</span>
                                  <span className="text-xs font-medium text-slate-600">{o.buyer_name}</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-8 py-5">
                              <div className="flex flex-col gap-2">
                                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg w-fit ${o.payment_mode === 'platform' ? 'bg-indigo-50 text-indigo-600' : 'bg-amber-50 text-amber-600'}`}>
                                  <SafeIcon icon={o.payment_mode === 'platform' ? FiCreditCard : FiDollarSign} className="text-[10px]" />
                                  <span className="text-[8px] font-black uppercase tracking-widest">{o.payment_mode}</span>
                                </div>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{o.delivery_method}</span>
                              </div>
                            </td>
                            <td className="px-8 py-5">
                              <p className="font-black text-slate-900 text-sm">{formatPrice(o.total_amount)}</p>
                            </td>
                            <td className="px-8 py-5">
                              <p className={`text-sm font-black ${
                                Number(o.refund_amount || 0) > 0
                                  ? 'text-rose-600'
                                  : 'text-slate-400'
                              }`}>
                                {formatPrice(o.refund_amount)}
                              </p>
                            </td>
                            <td className="px-8 py-5 text-right">
                              <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
                                o.status === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                                o.status === 'pending' ? 'bg-amber-50 text-amber-600' :
                                o.status === 'cancelled' || o.status === 'declined' ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
                              }`}>
                                {o.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </AdminTablePagination>
              </div>
            </motion.div>
          )}

          {activeTab === 'analytics' && (
            <motion.div key="analytics" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-8 border-b border-gray-50 flex justify-between items-center">
                <h3 className="text-xl font-black text-gray-900">Merchant Performance</h3>
                <div className="relative">
                  <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" placeholder="Filter merchants..." value={search} onChange={e => setSearch(e.target.value)} className="pl-12 pr-4 py-3 bg-gray-50 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500 border-none outline-none" />
                </div>
              </div>
              <AdminTablePagination>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/50 text-[10px] text-gray-400 uppercase tracking-widest">
                        <th className="px-8 py-4 text-left">Bakery / Owner</th>
                        <th className="px-8 py-4 text-left">Total Sales</th>
                        <th className="px-8 py-4 text-left">Orders</th>
                        <th className="px-8 py-4 text-left">Inventory</th>
                        <th className="px-8 py-4 text-left">Rating</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {tabLoading ? (
                        <tr><td colSpan="5" className="py-20 text-center"><FiLoader className="animate-spin inline text-indigo-300 text-3xl" /></td></tr>
                      ) : sellerAnalytics.filter(s => s.business_name.toLowerCase().includes(search.toLowerCase())).map(s => (
                        <tr key={s.seller_id}>
                          <td className="px-8 py-5">
                            <p className="font-bold text-gray-900 text-sm">{s.business_name}</p>
                            <p className="text-[10px] text-gray-400 font-bold uppercase">{s.owner_name}</p>
                          </td>
                          <td className="px-8 py-5">
                            <p className="font-black text-indigo-600 text-sm">{formatPrice(s.total_revenue)}</p>
                          </td>
                          <td className="px-8 py-5">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-slate-700">{s.completed_orders}</span>
                              <span className="text-[9px] text-slate-400 font-black uppercase">/ {s.total_orders} Total</span>
                            </div>
                          </td>
                          <td className="px-8 py-5">
                            <div className="flex items-center gap-2">
                              <SafeIcon icon={FiBox} className="text-slate-400 text-xs" />
                              <span className="text-sm font-bold text-slate-700">{s.active_listings} Active</span>
                            </div>
                          </td>
                          <td className="px-8 py-5">
                            <div className="flex items-center px-3 py-1 bg-amber-50 rounded-xl w-fit">
                              <SafeIcon icon={FiStar} className="text-amber-500 fill-current text-[10px] mr-1.5" />
                              <span className="text-xs font-black text-amber-700">{s.avg_rating || '0.0'}</span>
                              <span className="text-[9px] text-amber-400 font-bold ml-1">({s.review_count})</span>
                            </div>
                          </td>
                        </tr>
                      ))
                      }
                    </tbody>
                  </table>
                </div>
              </AdminTablePagination>
            </motion.div>
          )}

          {activeTab === 'finance' && (
            <motion.div key="finance" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
              {/* Stat Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Platform GMV</p>
                  <p className="text-3xl font-black text-indigo-600">{formatPrice(financeSummary?.total_sales)}</p>
                  <p className="text-[9px] font-bold text-slate-400 mt-2">Processed via SohLocal</p>
                </div>
                <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Platform Revenue (1%)</p>
                  <p className="text-3xl font-black text-emerald-600">{formatPrice(financeSummary?.total_platform_revenue)}</p>
                  <p className="text-[9px] font-bold text-slate-400 mt-2">Net Service Fees</p>
                </div>
                <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Stripe Fees (2.9% + 30¢)</p>
                  <p className="text-3xl font-black text-rose-500">{formatPrice(financeSummary?.total_stripe_fees)}</p>
                  <p className="text-[9px] font-bold text-slate-400 mt-2">External Processing Costs</p>
                </div>
                <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Collected Tax</p>
                  <p className="text-3xl font-black text-slate-900">{formatPrice(financeSummary?.total_tax)}</p>
                  <p className="text-[9px] font-bold text-slate-400 mt-2">Allocated for Remittance</p>
                </div>
              </div>

              {/* Transaction Log */}
              <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-8 border-b border-gray-50 flex justify-between items-center">
                  <h3 className="text-xl font-black text-gray-900">Platform Transactions</h3>
                  <span className="text-[10px] font-black bg-indigo-50 text-indigo-600 px-4 py-1.5 rounded-full uppercase tracking-widest">
                    {financeSummary?.processed_order_count || 0} Successful Payments
                  </span>
                </div>
                <AdminTablePagination>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50/50 text-[10px] text-gray-400 uppercase tracking-widest">
                          <th className="px-8 py-4 text-left">Date / Details</th>
                          <th className="px-8 py-4 text-left">Order Total</th>
                          <th className="px-8 py-4 text-left">Tax</th>
                          <th className="px-8 py-4 text-left">Stripe Fee</th>
                          <th className="px-8 py-4 text-left">Platform Fee</th>
                          <th className="px-8 py-4 text-right">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {tabLoading ? (
                          <tr><td colSpan="6" className="py-20 text-center"><FiLoader className="animate-spin inline text-indigo-300 text-3xl" /></td></tr>
                        ) : transactions.length === 0 ? (
                          <tr><td colSpan="6" className="py-20 text-center text-gray-400 font-bold uppercase text-[10px] tracking-widest">No platform transactions recorded</td></tr>
                        ) : transactions.map(tx => (
                          <tr key={tx.id}>
                            <td className="px-8 py-5">
                              <button type="button" onClick={() => openOrderDetails(tx.order_id || tx.id)} className="text-left">
                                <p className="font-black text-indigo-600 text-sm">#{tx.order_reference || String(tx.order_id || tx.id).slice(0, 8).toUpperCase()}</p>
                                <p className="font-bold text-gray-900 text-sm">{tx.business_name}</p>
                                <p className="text-[9px] text-gray-400 font-black uppercase">Buyer: {tx.buyer_name}</p>
                                <p className="text-[9px] text-indigo-400 font-bold uppercase mt-0.5">{new Date(tx.created_at).toLocaleDateString()}</p>
                              </button>
                            </td>
                            <td className="px-8 py-5 font-black text-slate-900 text-sm">{formatPrice(tx.total_amount)}</td>
                            <td className="px-8 py-5 font-bold text-slate-500 text-xs">{formatPrice(tx.tax_amount)}</td>
                            <td className="px-8 py-5 font-bold text-rose-500 text-xs">{formatPrice(tx.stripe_fee)}</td>
                            <td className="px-8 py-5 font-black text-emerald-600 text-xs">{formatPrice(tx.platform_fee)}</td>
                            <td className="px-8 py-5 text-right">
                              <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[9px] font-black uppercase tracking-widest">
                                {tx.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </AdminTablePagination>
              </div>
            </motion.div>
          )}

          {activeTab === 'users' && (
            <motion.div key="users" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-8 border-b border-gray-50 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h3 className="text-xl font-black text-gray-900">User Management</h3>
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  <div className="relative flex-1 sm:w-64">
                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-gray-50 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500 border-none outline-none" />
                  </div>
                </div>
              </div>
              <AdminTablePagination>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/50 text-[10px] text-gray-400 uppercase tracking-widest">
                        <th className="px-8 py-4 text-left">User</th>
                        <th className="px-8 py-4 text-left">Role / Status</th>
                        <th className="px-8 py-4 text-left">Payment Mode</th>
                        <th className="px-8 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {tabLoading ? (
                        <tr><td colSpan="4" className="py-20 text-center"><FiLoader className="animate-spin inline text-indigo-300 text-3xl" /></td></tr>
                      ) : allUsers
                          .filter(u => u.email.toLowerCase().includes(search.toLowerCase()) || u.name.toLowerCase().includes(search.toLowerCase()))
                          .map(u => (
                        <tr key={u.id}>
                          <td className="px-8 py-5">
                            <p className="font-bold text-gray-900 text-sm">{u.name}</p>
                            <p className="text-[10px] text-gray-400 font-bold uppercase">{u.email}</p>
                          </td>
                          <td className="px-8 py-5">
                            <div className="flex flex-col items-start gap-2">
                              <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${u.role === 'admin' ? 'bg-red-50 text-red-600' : u.is_seller ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                                {u.role}
                              </span>
                              {userStatuses[u.id] && (
                                <span className="rounded-full bg-rose-50 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-rose-600">
                                  Sign-in disabled
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-8 py-5">
                            {u.is_seller ? (
                              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl w-fit ${u.payment_mode === 'platform' ? 'bg-indigo-50 text-indigo-700' : 'bg-amber-50 text-amber-700'}`}>
                                <SafeIcon icon={u.payment_mode === 'platform' ? FiCreditCard : FiDollarSign} className="text-xs" />
                                <span className="text-[9px] font-black uppercase tracking-widest">
                                  {u.payment_mode === 'platform' ? 'Platform' : 'Direct'}
                                </span>
                              </div>
                            ) : (
                              <span className="text-[10px] text-gray-300 font-bold uppercase tracking-widest">N/A</span>
                            )}
                          </td>
                          <td className="px-8 py-5 text-right space-x-2">
                            <button onClick={() => handleUpdateRole(u.id, u.role === 'admin' ? 'buyer' : 'admin', false)} className="p-2.5 bg-gray-50 text-gray-400 hover:text-indigo-600 rounded-xl transition-all" title="Toggle Admin Role" aria-label="Toggle Admin Role">
                              <SafeIcon icon={FiShield} />
                            </button>
                            {u.role !== 'admin' && (
                              <>
                                <button
                                  onClick={() => handleToggleUserDisabled(u)}
                                  className={`p-2.5 rounded-xl transition-all ${userStatuses[u.id] ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white' : 'bg-amber-50 text-amber-600 hover:bg-amber-600 hover:text-white'}`}
                                  title={userStatuses[u.id] ? 'Restore sign-in' : 'Disable sign-in'}
                                  aria-label={userStatuses[u.id] ? `Restore sign-in for ${u.email}` : `Disable sign-in for ${u.email}`}
                                >
                                  <SafeIcon icon={userStatuses[u.id] ? FiUnlock : FiLock} />
                                </button>
                              </>
                            )}
                            <button onClick={() => handleSendPasswordReset(u)} className="p-2.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white rounded-xl transition-all" title="Send password reset email" aria-label={`Send password reset email to ${u.email}`}>
                              <SafeIcon icon={FiMail} />
                            </button>
                            <button onClick={() => handleDeleteUser(u)} className="p-2.5 bg-gray-50 text-gray-400 hover:bg-red-600 hover:text-white rounded-xl transition-all" title="Delete Account" aria-label={`Delete account for ${u.email}`}>
                              <SafeIcon icon={FiTrash2} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AdminTablePagination>
            </motion.div>
          )}

          {activeTab === 'listings' && (
            <motion.div key="listings" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-8 border-b border-gray-50 flex justify-between items-center">
                <h3 className="text-xl font-black text-gray-900">Inventory</h3>
                <div className="relative">
                  <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" placeholder="Filter listings..." value={search} onChange={e => setSearch(e.target.value)} className="pl-12 pr-4 py-3 bg-gray-50 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500 border-none outline-none" />
                </div>
              </div>
              <AdminTablePagination>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/50 text-[10px] text-gray-400 uppercase tracking-widest">
                        <th className="px-8 py-4 text-left">Item</th>
                        <th className="px-8 py-4 text-left">Seller</th>
                        <th className="px-8 py-4 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {tabLoading ? (
                        <tr><td colSpan="3" className="py-20 text-center"><FiLoader className="animate-spin inline text-3xl text-indigo-300" /></td></tr>
                      ) : allOfferings.filter(o => o.title.toLowerCase().includes(search.toLowerCase())).map(o => (
                        <tr key={o.id}>
                          <td className="px-8 py-5">
                            <p className="font-bold text-gray-900 text-sm">{o.title}</p>
                            <p className="text-[10px] text-gray-400 font-bold uppercase">{o.category} • {o.base_price}</p>
                          </td>
                          <td className="px-8 py-5 text-gray-600 text-sm">{o.seller_business_name}</td>
                          <td className="px-8 py-5 text-right space-x-2">
                            <button onClick={() => handleToggleListing(o)} className={`p-3 rounded-xl transition-all ${o.is_active ? 'bg-gray-50 text-gray-400 hover:text-amber-600' : 'bg-indigo-50 text-indigo-600'}`}>
                              {o.is_active ? <FiEyeOff title="Deactivate Listing" /> : <FiEye title="Activate Listing" />}
                            </button>
                            <button onClick={() => handleDeleteListing(o.id, o.title)} className="p-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-600 hover:text-white transition-all">
                              <FiTrash2 title="Permanent Delete" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AdminTablePagination>
            </motion.div>
          )}

          {activeTab === 'reviews' && (
            <motion.div key="reviews" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-8 border-b border-gray-50"><h3 className="text-xl font-black text-gray-900">Feedback Moderation</h3></div>
              <AdminTablePagination>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/50 text-[10px] text-gray-400 uppercase tracking-widest">
                        <th className="px-8 py-4 text-left">Review</th>
                        <th className="px-8 py-4 text-left">Context</th>
                        <th className="px-8 py-4 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {tabLoading ? (
                        <tr><td colSpan="3" className="py-20 text-center"><FiLoader className="animate-spin inline text-indigo-300 text-3xl" /></td></tr>
                      ) : allReviews.length === 0 ? (
                        <tr><td colSpan="3" className="py-20 text-center text-gray-400 font-bold uppercase text-[10px] tracking-widest">No reviews to display</td></tr>
                      ) : allReviews.map(r => (
                        <tr key={r.id}>
                          <td className="px-8 py-5 max-w-xs">
                            <div className="flex text-amber-400 mb-1">
                              {[...Array(r.rating)].map((_, i) => <FiStar key={i} className="fill-current text-[10px]" />)}
                            </div>
                            <p className="text-sm text-gray-600 italic line-clamp-2">"{r.comment || 'No comment provided'}"</p>
                          </td>
                          <td className="px-8 py-5">
                            <p className="text-xs font-bold text-gray-900">{r.buyer_name}</p>
                            <p className="text-[9px] text-gray-400 uppercase font-black">Target: {r.seller_business_name}</p>
                          </td>
                          <td className="px-8 py-5 text-right">
                            <button onClick={() => handleDeleteReview(r.id)} className="p-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-600 hover:text-white transition-all">
                              <FiTrash2 title="Delete Review" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AdminTablePagination>
            </motion.div>
          )}

          {activeTab === 'infrastructure' && (
            <motion.div key="infrastructure" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="bg-white rounded-[2.5rem] p-10 border border-gray-100">
                <h3 className="text-xl font-black text-gray-900 mb-8">System Infrastructure</h3>
                <div className="flex items-center justify-between p-6 bg-gray-50 rounded-3xl">
                  <div className="flex items-center space-x-4">
                    <FiCloud className="text-blue-500 text-2xl" />
                    <div>
                      <p className="text-sm font-black">Cloudflare R2 Storage</p>
                      <p className="text-[10px] text-gray-400 font-bold uppercase">{r2Status.message}</p>
                    </div>
                  </div>
                  {r2Status.connected ? <FiCheckCircle className="text-emerald-500" /> : <FiAlertCircle className="text-red-500" />}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {userDeletionTarget && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-[110] p-4">
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-user-title"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-[2.5rem] max-w-lg w-full p-8 sm:p-10 shadow-2xl"
            >
              <div className="flex justify-between items-start gap-4 mb-6">
                <div>
                  <h3 id="delete-user-title" className="text-2xl font-black text-gray-900">Confirm User Deletion</h3>
                  <p className="text-xs text-slate-500 font-medium mt-2">
                    This permanently removes this user and their associated account data.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeUserDeletionModal}
                  disabled={userDeletionLoading}
                  className="text-slate-400 hover:text-gray-900 disabled:opacity-50"
                  aria-label="Close deletion confirmation"
                >
                  <SafeIcon icon={FiX} className="text-2xl" />
                </button>
              </div>

              <div className="rounded-2xl bg-red-50 border border-red-100 p-4 mb-6">
                <p className="text-[10px] font-black text-red-600 uppercase tracking-widest">Account to delete</p>
                <p className="text-sm font-bold text-gray-900 mt-1">{userDeletionTarget.name}</p>
                <p className="text-xs text-slate-600 mt-1">{userDeletionTarget.email}</p>
              </div>

              <form onSubmit={handleConfirmDeleteUser} className="space-y-5">
                <div>
                  <label htmlFor="delete-confirmation-email" className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
                    Type the account email to confirm
                  </label>
                  <input
                    id="delete-confirmation-email"
                    type="email"
                    autoComplete="off"
                    required
                    value={userDeletionEmail}
                    onChange={event => setUserDeletionEmail(event.target.value)}
                    className="w-full px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold border border-gray-100 outline-none focus:ring-2 focus:ring-red-500"
                    placeholder={userDeletionTarget.email}
                  />
                </div>

                <div>
                  <label htmlFor="admin-delete-pin" className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
                    Admin PIN
                  </label>
                  <input
                    id="admin-delete-pin"
                    type="password"
                    inputMode="numeric"
                    autoComplete="current-password"
                    pattern="[0-9]{8,12}"
                    minLength={8}
                    maxLength={12}
                    required
                    value={userDeletionPin}
                    onChange={event => setUserDeletionPin(event.target.value.replace(/\D/g, '').slice(0, 12))}
                    className="w-full px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold border border-gray-100 outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Enter your 8–12 digit PIN"
                  />
                </div>

                {userDeletionError && (
                  <p role="alert" className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm font-bold text-red-700">
                    {userDeletionError}
                  </p>
                )}

                <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeUserDeletionModal}
                    disabled={userDeletionLoading}
                    className="flex-1 py-4 bg-gray-100 text-gray-600 rounded-2xl font-black text-[10px] uppercase tracking-widest disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={userDeletionLoading}
                    className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-red-100 disabled:opacity-50"
                  >
                    {userDeletionLoading ? 'Deleting Account...' : 'Confirm Permanent Deletion'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        <AdminOrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      </div>
    </div>
  );
};

export default AdminDashboardPage;
